/**
 * Observer the target for additions to the DOM. If an addition is detected, the observer will call the handler.
 * This module specifically looks for DOM elements with the specified attribute and generates the element's contents in the specified locale.
 * The locale can be contained in the mark up but the normal use case is to use the selected locale from the Locale module.
 * This also listens for changes to the selected locale and updates all nodes with the specified attribute.
 *
 * NOTE: There is a helper method in Template.js called dateTimeLocale and dateTimeRangeLocale to help generate the markup for this observer to use.
 * @module DateTimeLocaleObserver
 */
inherit("DateTimeLocaleObserver", {
	parent: `Class`,
	singleton: true,
	requires: [`DOM`, `Locale`, `Time`],
	destructor() {
		this.unobserve();
	},
	api: {
		/**
		 * @typedef DateTimeLocaleObserverOptions
		 * @property {HTMLElement} target - The target element to observe. Defaults to `document.body`.
		 * @property {boolean} includeChildren - Whether to include children of the target element. Defaults to `true`.
		 * @property {string} localeTimeAttribute - The attribute name check for the locale timestamp. Defaults to `data-locale-time`.
		 */
		/**
		 * @method DateTimeLocaleObserver#observe
		 * @public
		 * @param {DateTimeLocaleObserverOptions} [options]
		 * @param {string} options.localeTimeAttribute - The attribute name to use for the locale time. Defaults to `data-locale-time`. NOTE: If this is changed, the attribute name in Templates must also be updated.
		 */
		observe: {
			public: true,
			value(_, options = {}) {
				if (this.observer) {
					this.log({
						type: `warn`,
						message: `DateTimeLocaleObserver:observe - Only one observer is supported.`,
					});

					return; // already have an observer
				}

				this.defaults(options, {
					target: document.body, // default to the document body
					includeChildren: true, // default to include children
					localeTimeAttribute: `data-locale-time`, // default attribute name, if this is changed, the attribute name in Templates must also be updated
				});

				const { data, Locale } = this;
				const { target, includeChildren, localeTimeAttribute } = options;

				this.assign(data, {
					target,
					includeChildren,
					localeTimeAttribute: localeTimeAttribute,
					localeTimeSelector: `[${localeTimeAttribute}]`,
				});

				// Create the observer and assign it to `this`
				// Do not allow it to be changed
				Object.defineProperty(this, `observer`, {
					value: new MutationObserver(this.handleMutations),
					writable: false,
					enumerable: false,
					configurable: false,
				});

				// Listen for changes to the selected locale
				Locale.get({
					fireImmediately: true,
					subscribe: ({ selected }) => {
						data.defaultLocale = selected.localizeJsKey;

						this.updateAll(); // update all nodes
					},
				});

				// Observe the target
				this.observer.observe(target, {
					childList: true,
					subtree: true,
				});
			},
		},

		/**
		 * Stops observing for changes
		 * @method DateTimeLocaleObserver#unobserve
		 * @public
		 */
		unobserve: {
			public: true,
			value() {
				if (this.observer) {
					this.observer.disconnect();
				}
			},
		},

		/**
		 * Handles mutations from the Observer callback
		 * @method DateTimeLocaleObserver#handleMutations
		 * @private
		 * @param {MutationRecord[]} mutations - The mutations to handle
		 */
		handleMutations: {
			private: true,
			value(mutations) {
				const { includeChildren, localeTimeAttribute, localeTimeSelector } = this.data;

				mutations.forEach((mutation) => {
					// only handle added nodes
					if (mutation.addedNodes.length) {
						mutation.addedNodes.forEach((node) => {
							if (!this.isElement(node)) return; // only handle elements

							// check current node
							if (node.hasAttribute(localeTimeAttribute)) {
								this.updateNode(node);
							}

							// check child nodes
							if (includeChildren && node.childNodes.length > 0) {
								const childNodes = this.DOM.select(localeTimeSelector, node);

								childNodes.forEach(this.updateNode);
							}
						});
					}
				});
			},
		},

		/**
		 * Updates all nodes with the specified attribute.
		 * @method DateTimeLocaleObserver#updateAll
		 */
		updateAll: {
			public: true,
			value() {
				const { data } = this;
				const { localeTimeSelector, target } = data;

				const nodes = this.DOM.select(localeTimeSelector, target);

				nodes.forEach(this.updateNode);
			},
		},

		/**
		 * Updates a single node with the specified attribute.
		 * @see Templates.dateTimeLocale to see how the markup is generated.
		 * The target node may have the following attributes:
		 * - data-locale-time - The timestamp to format. If omitted, the current time is used.
		 * - data-locale-time-end - The end timestamp to format. If included, a range is formatted.
		 * - data-timezone - The timezone to use. If omitted, the Time.formatLocale will determine the timezone
		 * - data-format - The format to use. If omitted, the Time.formatLocale will determine the format
		 * - data-locale - The locale to use. If omitted, the selected locale from Locale.get will be used.
		 *
		 * @method DateTimeLocaleObserver#updateNode
		 * @param {HTMLElement} target - The target node to update
		 */
		updateNode: {
			value(target) {
				const { data } = this;
				const { defaultLocale } = data;
				const { dataset } = target;
				const { localeTime, localeTimeEnd, timezone, format } = dataset;
				const locale = dataset.locale || defaultLocale; // if no locale is specified, use the default locale
				const timestamp = parseInt(localeTime, 10);
				let localeString;

				if (localeTimeEnd) {
					const timestampEnd = parseInt(localeTimeEnd, 10);

					localeString = this.Time.formatLocaleRange(format, timestamp, timestampEnd, timezone, locale);
				} else {
					localeString = this.Time.formatLocale(format, timestamp, timezone, locale);
				}

				target.innerText = localeString;
			},
		},
	},
});

//# sourceMappingURL=DateTimeLocaleObserver.js.map